/**
 * Entry point for the merged app: shell document, or app document.
 *
 * **Changed 2026-08-28, by request** — back to the two-bootstrap split that
 * `bitsonic-portal` uses, and away from 6.3's route-level shell.
 *
 * ### What 6.3 did, and why it was not enough
 *
 * The original app made this choice here, before anything else existed, because there was
 * only one app. The merge could not keep that as written: it would have framed Admin, Auth,
 * QC and WebService, none of which were designed for a fixed-width iframe, and all four came
 * up blank inside it. So the shell moved into the `/portal` route and this file just mounted
 * the router.
 *
 * That fixed the four apps and broke the shell's *document*. Reaching a route means booting
 * the whole application first, so the outer document — whose only job is to hold one iframe —
 * loaded Redux, the router, `SessionBoot`, telemetry, all eight app stylesheets and Bulma,
 * then downloaded every one of them again inside the frame. The measured symptom was the
 * zoom: the reference's shell document has nothing on `<html>`/`<body>` but `shell.css`,
 * this one had Bulma's `html`/`body` block underneath it, and Chrome page zoom did not
 * produce the reference's black bars.
 *
 * ### What this does instead
 *
 * The choice is made here again, from the URL alone, and each branch is a **dynamic** import
 * so the shell document never downloads the app bundle at all:
 *
 *   * already inside the frame (or `?__bs_viewport=1`) → `bootstrapApp` — the real app
 *   * otherwise → `bootstrapShell` — one iframe of this same URL, and nothing else
 *
 * The shell now wraps **every** app, not just the portal (requested 2026-08-28). The 6.3
 * blank-app finding does not apply the way it did: it came from framing routes *inside* an
 * already-booted document, whereas the child here is a plain top-level document load of the
 * same URL — the same thing the portal has been doing since the shell existed.
 *
 * `/qc` keeps working when embedded in someone else's platform without any special case:
 * `isDesignViewportApp()` is true for any framed document, so an embedded QC is the app,
 * never a second shell around one.
 */

import { isDesignViewportApp } from "./viewport/computeDesignViewport";

if (isDesignViewportApp()) {
  import("./bootstrapApp").then((m) => m.default());
} else {
  import("./bootstrapShell").then((m) => m.default());
}
